import { observer } from 'owa-mobx-react';
import React from 'react';
import type { MailFolderListProps } from 'owa-mail-folder-view';
import type { FolderForestTreeType } from 'owa-graph-schema';
import type { AriaProperties } from 'owa-accessibility';
import { MailFolderList } from 'owa-mail-folder-view';
import { generateDomPropertiesForAria, AriaRoles } from 'owa-accessibility';
import MailFolderNodeTree from './MailFolderNodeTree';
import type { MailboxInfo } from 'owa-client-types';
import type { MailFolderListWrapperFragment } from 'owa-mail-folder-view-graphql';
import LoadMore from './LoadMore';
import { logGreyError } from 'owa-analytics';
import { setFoldersDataCollectionInformation } from 'owa-folders/lib/util/registerDataCollector';

// Stable empty array reference to avoid unnecessary re-renders when childFolderIds is not yet loaded
const EMPTY_FOLDER_IDS: string[] = [];

export interface MailFolderListWrapperProps extends React.HTMLProps<HTMLDivElement> {
    isFavoritesSupported: boolean; // this tree's folders can or can't support favorites for folder
    rootFolder: MailFolderListWrapperFragment;
    shouldShowLoadingSpinner: boolean;
    hasMoreData: boolean;
    treeType: FolderForestTreeType; // Type of folder tree
    rootNodeId: string;
    mailboxInfo: MailboxInfo;
    moveFolder: (
        destinationFolderId: string,
        destinationFolderMailboxInfo: MailboxInfo,
        sourceFolderId: string,
        sourceFolderMailboxInfo: MailboxInfo,
        sourceFolderParentFolderId: string,
        sourceFolderDisplayName: string
    ) => void;
    offset: number;
    fetchMore: (offset: number) => Promise<void>;
    deleteFolder: (
        folderId: string,
        isSearchFolder?: boolean,
        distinguishedFolderParentIds?: string[]
    ) => void;
    nestDepth?: number;
    ellipsesOnHover?: boolean;
    ariaLabelledBy?: string;
}

/**
 * Wrapper component that reads mobx store specific values for folder hierarchy
 */
export const MailFolderListWrapper: React.FunctionComponent<MailFolderListWrapperProps> = observer(
    function MailFolderListWrapper(props: MailFolderListWrapperProps) {
        const containerRef = React.useRef<HTMLDivElement | null>(null);
        const {
            className,
            style,
            mailboxInfo,
            moveFolder,
            deleteFolder,
            ellipsesOnHover,
            fetchMore,
            shouldShowLoadingSpinner,
            hasMoreData,
            offset,
            rootFolder,
        } = props;

        // Read childFolderIds directly (no useMemo) so MobX always tracks the observable
        // access on every render. The previous useMemo could cache a stale empty array when
        // the component mounts inside CSSTransition (unmountOnExit) before folder data loads.
        const childFolderIds = rootFolder?.childFolderIds ?? EMPTY_FOLDER_IDS;

        // Diagnostic: log once per mount if the folder tree is empty while not loading.
        const hasLoggedEmptyRef = React.useRef(false);

        if (
            childFolderIds.length === 0 &&
            rootFolder?.id &&
            !shouldShowLoadingSpinner &&
            !hasLoggedEmptyRef.current
        ) {
            hasLoggedEmptyRef.current = true;
            logGreyError(
                'FolderTreeEmptyOnRender',
                mailboxInfo,
                new Error('FolderTreeEmptyOnRender'),
                {
                    treeType: props.treeType,
                }
            );
            setFoldersDataCollectionInformation(
                mailboxInfo.mailboxSmtpAddress + '_emptyTree',
                'FolderTreeEmptyOnRender: rootFolderId=' + rootFolder.id
            );
        }

        const renderFolderNode = React.useCallback(
            (
                folderId: string,
                nestDepth: number,
                treeTypeInput: FolderForestTreeType,
                isFavoritesSupported: boolean,
                onContextMenu: (
                    evt: React.MouseEvent<unknown> | React.TouchEvent<unknown>,
                    folderId: string,
                    distinguishedFolderParentIds: string[]
                ) => void,
                distinguishedFolderParentIds?: string[],
                parentRef?: React.RefObject<HTMLDivElement>
            ): JSX.Element => {
                return (
                    <MailFolderNodeTree
                        key={folderId}
                        folderId={folderId}
                        nestDepth={nestDepth}
                        treeType={treeTypeInput}
                        isFavoritesSupported={isFavoritesSupported}
                        onContextMenu={onContextMenu}
                        renderFolderNode={renderFolderNode}
                        mailboxInfo={mailboxInfo}
                        distinguishedFolderParentIds={distinguishedFolderParentIds}
                        parentRef={parentRef}
                        moveFolder={moveFolder}
                        deleteFolder={deleteFolder}
                        ellipsesOnHover={ellipsesOnHover}
                    />
                );
            },
            [mailboxInfo, moveFolder, deleteFolder, ellipsesOnHover]
        );

        const renderLoadNode = React.useCallback(() => {
            return (
                <LoadMore
                    fetchMore={fetchMore}
                    shouldShowLoadingSpinner={shouldShowLoadingSpinner}
                    hasMoreData={hasMoreData}
                    offset={offset}
                />
            );
        }, [fetchMore, shouldShowLoadingSpinner, hasMoreData, offset]);

        const mailFolderListProps: MailFolderListProps = {
            ...props,
            rootFolderId: rootFolder?.id,
            renderFolderNode,
            childFolderIds,
            renderLoadNode,
            nestDepth: props.nestDepth,
        };

        const ariaProps: AriaProperties = {
            role: AriaRoles.group,
            labelledBy: props.ariaLabelledBy,
        };

        return (
            <div
                ref={containerRef}
                style={style}
                className={className}
                {...generateDomPropertiesForAria(ariaProps)}
            >
                {
                    /* Render tree */
                    <MailFolderList {...mailFolderListProps} />
                }
            </div>
        );
    },
    'MailFolderListWrapper'
);
